import {
  createContext,
  PropsWithChildren,
  useContext,
  useRef,
  useSyncExternalStore,
} from 'react'

interface ErrorAccumulatorSnapshot {
  count: number
  escalatorId: symbol | null
}

interface ErrorAccumulatorStore {
  subscribe: (listener: () => void) => () => void
  getSnapshot: () => ErrorAccumulatorSnapshot
  increment: () => void
  decrement: () => void
  claimEscalation: (id: symbol) => boolean
  releaseEscalation: (id: symbol) => void
}

const createErrorAccumulatorStore = (): ErrorAccumulatorStore => {
  let count = 0
  let escalatorId: symbol | null = null
  const listeners = new Set<() => void>()
  let snapshot: ErrorAccumulatorSnapshot = { count: 0, escalatorId: null }

  const notify = () => {
    snapshot = { count, escalatorId }
    listeners.forEach((l) => l())
  }

  return {
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    getSnapshot: () => snapshot,
    increment: () => {
      count++
      notify()
    },
    decrement: () => {
      count = Math.max(0, count - 1)
      if (count < ERROR_THRESHOLD) {
        escalatorId = null
      }
      notify()
    },
    claimEscalation: (id: symbol) => {
      if (escalatorId !== null) return false
      escalatorId = id
      notify()
      return true
    },
    releaseEscalation: (id: symbol) => {
      if (escalatorId !== id) return
      escalatorId = null
      notify()
    },
  }
}

/**
 * Module-level singleton store shared by all CompactErrorFallback instances
 * that don't have an explicit ErrorAccumulatorProvider ancestor. This ensures
 * error accumulation works even when the page-level template boundary uses
 * withErrorBoundary directly instead of PlatformErrorBoundary.
 */
const globalStore = createErrorAccumulatorStore()

const ErrorAccumulatorContext =
  createContext<ErrorAccumulatorStore>(globalStore)

export const ERROR_THRESHOLD = 3

export const ErrorAccumulatorProvider = ({ children }: PropsWithChildren) => {
  const storeRef = useRef<ErrorAccumulatorStore>(createErrorAccumulatorStore())

  return (
    <ErrorAccumulatorContext.Provider value={storeRef.current}>
      {children}
    </ErrorAccumulatorContext.Provider>
  )
}

export const useErrorAccumulator = () => {
  const store = useContext(ErrorAccumulatorContext)
  const { count, escalatorId } = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
  )

  return {
    count,
    escalatorId,
    increment: store.increment,
    decrement: store.decrement,
    claimEscalation: store.claimEscalation,
    releaseEscalation: store.releaseEscalation,
  }
}
